import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  User,
  Calendar,
  MessageSquare,
  Users,
  Mail,
  ExternalLink,
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';

export default function AgentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        setIsLoading(true);
        const response = await adminApi.getAgent(id);
        setAgent(response.agent);
      } catch (err) {
        console.error('Failed to fetch agent:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAgent();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-bg-secondary rounded w-1/4"></div>
          <div className="card p-6">
            <div className="h-20 bg-bg-secondary rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto text-center">
        <p className="text-text-muted">Agent not found</p>
        <button onClick={() => navigate('/agents')} className="btn-primary mt-4">
          Back to Agents
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate('/agents')}
        className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-6"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Agents
      </button>

      {/* Agent Header */}
      <div className="card p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Bot className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">{agent.name}</h1>
              <p className="text-text-secondary">{agent.businessType || 'AI Agent'}</p>
              <div className="flex items-center gap-4 mt-2">
                <span
                  className={`badge ${
                    agent.status === 'active'
                      ? 'badge-success'
                      : agent.status === 'paused'
                      ? 'badge-warning'
                      : 'badge-gray'
                  }`}
                >
                  {agent.status}
                </span>
                <span
                  className={`badge ${
                    agent.whatsappStatus === 'connected' ? 'badge-success' : 'badge-gray'
                  }`}
                >
                  WhatsApp {agent.whatsappStatus === 'connected' ? 'Connected' : 'Not Connected'}
                  {agent.whatsappStatus === 'connected' && agent.whatsappPhoneNumber && (
                    <span className="ml-1">({agent.whatsappPhoneNumber})</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Link
          to={`/conversations?agent_id=${id}`}
          className="card p-4 text-center hover:border-primary hover:shadow-md transition-all"
        >
          <MessageSquare className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-text-primary">
            {agent.conversationsCount || 0}
          </p>
          <p className="text-sm text-text-muted flex items-center justify-center gap-1">
            Conversations
            <ExternalLink className="w-3 h-3" />
          </p>
        </Link>
        <Link
          to={`/leads?agent_id=${id}`}
          className="card p-4 text-center hover:border-primary hover:shadow-md transition-all"
        >
          <Users className="w-6 h-6 text-green-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-text-primary">
            {agent.leadsCount || 0}
          </p>
          <p className="text-sm text-text-muted flex items-center justify-center gap-1">
            Leads
            <ExternalLink className="w-3 h-3" />
          </p>
        </Link>
        <Link
          to={`/appointments?agent_id=${id}`}
          className="card p-4 text-center hover:border-primary hover:shadow-md transition-all"
        >
          <Calendar className="w-6 h-6 text-blue-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-text-primary">
            {agent.appointmentsCount || 0}
          </p>
          <p className="text-sm text-text-muted flex items-center justify-center gap-1">
            Appointments
            <ExternalLink className="w-3 h-3" />
          </p>
        </Link>
        <Link
          to={`/emails?agent_id=${id}`}
          className="card p-4 text-center hover:border-primary hover:shadow-md transition-all"
        >
          <Mail className="w-6 h-6 text-amber-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-text-primary">
            {agent.emailsCount || 0}
          </p>
          <p className="text-sm text-text-muted flex items-center justify-center gap-1">
            Emails
            <ExternalLink className="w-3 h-3" />
          </p>
        </Link>
      </div>

      {/* Owner Info */}
      <div className="card p-6 mb-6">
        <h2 className="font-semibold text-text-primary mb-4">Owner Information</h2>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-6 h-6 text-primary" />
          </div>
          <div>
            <p className="font-medium text-text-primary">{agent.ownerName || 'Unknown'}</p>
            <p className="text-sm text-text-secondary">{agent.ownerEmail}</p>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="card p-6">
        <h2 className="font-semibold text-text-primary mb-4">Agent Details</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-text-muted">Created</p>
              <p className="font-medium text-text-primary">{formatDate(agent.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm text-text-muted">Last Updated</p>
              <p className="font-medium text-text-primary">{formatDate(agent.updatedAt)}</p>
            </div>
            <div>
              <p className="text-sm text-text-muted">Model</p>
              <p className="font-medium text-text-primary">{agent.model || 'gpt-4o-mini'}</p>
            </div>
            <div>
              <p className="text-sm text-text-muted">Language</p>
              <p className="font-medium text-text-primary">{agent.language || 'English'}</p>
            </div>
          </div>
          {agent.description && (
            <div>
              <p className="text-sm text-text-muted mb-1">Description</p>
              <p className="text-text-primary">{agent.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
